import * as React from 'react';
import { cn } from './lib/utils';

/**
 * Segmented pill toggle — replaces inline button arrays with hardcoded colors.
 *
 * <FilterToggle
 *   value={filterStatus}
 *   onChange={setFilterStatus}
 *   options={[
 *     { value: 'all',      label: 'All' },
 *     { value: 'active',   label: 'Active',   count: 12 },
 *     { value: 'inactive', label: 'Inactive', count: 3 },
 *   ]}
 * />
 *
 * Each option may include an optional `count` that renders as a small badge.
 *
 * size="sm"  → text-[11px], h-6 pills (default)
 * size="md"  → text-body (13px), h-8 pills
 */

const SIZE = {
  sm: { pill: 'h-6 px-2.5 text-[11px]', badge: 'text-[10px] px-1' },
  md: { pill: 'h-8 px-3 text-body',     badge: 'text-[11px] px-1.5' },
};

function FilterToggle({ value, onChange, options = [], size = 'sm', className }) {
  const s = SIZE[size] ?? SIZE.md;

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-lg border border-nn-border bg-surface p-0.5',
        className
      )}
      role="group"
    >
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              'inline-flex items-center gap-1.5 rounded font-medium transition-all',
              s.pill,
              active
                ? 'bg-nn-white text-text-dark shadow-sm'
                : 'text-text-dark-secondary hover:text-text-dark'
            )}
          >
            {opt.label}
            {opt.count !== undefined && (
              <span
                className={cn(
                  'rounded font-semibold leading-none py-0.5',
                  s.badge,
                  active ? 'bg-aqua-glow text-aqua' : 'bg-surface-warm text-text-muted'
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export { FilterToggle };
